import { getToken } from 'firebase/app-check';
import { auth, getAppCheckInstance } from './firebase';
import { TRACE_HEADER, getTraceId } from './logger';
import { readActiveOrgId } from './active-org';

/**
 * Build the headers any authenticated `/api/*` request needs:
 *   - X-Request-Id for client/server log correlation
 *   - Authorization: Bearer <id-token> when a user is signed in
 *   - X-Firebase-AppCheck: attested-origin token when App Check is wired
 *
 * Awaits `auth.authStateReady()` so requests firing on first paint don't
 * race the Firebase Auth SDK rehydrating from IndexedDB and ship without
 * a Bearer token (would 401 the signed-in user).
 *
 * App Check is best-effort: failures here never block the request. The
 * server's guard logs a warn when the header is missing and only rejects
 * once `APP_CHECK_ENFORCE=true` is set on the Functions runtime.
 */
/**
 * Typed signal thrown by `authHeaders()` when `getIdToken()` fails in a
 * way that indicates the Firebase Auth SDK is stuck (corrupted refresh
 * token, `securetoken.googleapis.com` 400s, etc.). Callers can
 * `instanceof SessionExpiredError` to surface a "your session expired —
 * please sign in again" UX instead of the generic "request failed"
 * fallback. Distinct from `ApiError` (server-side failures); this fires
 * before any request hits the wire.
 *
 * `authHeaders()` also triggers an inline recovery path (purge IndexedDB
 * + redirect to `/sign-in?reason=session-expired`) so even callers that
 * don't catch this type get the user unstuck — the next sign-in mints a
 * fresh refresh token from scratch.
 */
export class SessionExpiredError extends Error {
  constructor(cause: unknown) {
    super('Firebase Auth session expired or corrupted; redirecting to /sign-in', { cause });
    this.name = 'SessionExpiredError';
  }
}

/**
 * Force-clear the Firebase Auth IndexedDB store + localStorage shim.
 * Mirrors `purgeFirebaseAuthIndexedDb` in
 * `components/providers/auth-provider.tsx` — kept inline here so the
 * non-React `authHeaders()` recovery path doesn't need to import a
 * React-only module. If the two ever drift, the auth-provider version
 * is the source of truth (it's the one the user-facing sign-out button
 * calls). Idempotent + browser-only.
 */
async function forceAuthPurge(): Promise<void> {
  if (typeof window === 'undefined') return;
  // Delete the IndexedDB store if present. When IndexedDB is blocked or
  // unavailable (incognito / iframe / older browser) the SDK falls back
  // to localStorage — so the localStorage sweep below must run
  // independently of this guard, otherwise the very environments most
  // likely to land in a wedged state get no recovery.
  if (window.indexedDB) {
    await new Promise<void>((resolve) => {
      const req = window.indexedDB.deleteDatabase('firebaseLocalStorageDb');
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
      req.onblocked = () => resolve();
    });
  }
  try {
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith('firebase:authUser:')) {
        window.localStorage.removeItem(key);
      }
    }
  } catch {
    // Private-mode / iframe quota issues are non-fatal; the redirect below
    // gets the user to a clean state regardless.
  }
}

/**
 * Single-shot guard so a flurry of concurrent `/api/*` fetches that all
 * fail on the same wedged token don't each fire their own purge +
 * redirect. The first one wins; subsequent calls within the same tab
 * lifecycle still throw `SessionExpiredError` but skip the side effects.
 */
let recoveryInFlight = false;

export async function authHeaders(): Promise<Record<string, string>> {
  await auth.authStateReady();
  const headers: Record<string, string> = {
    [TRACE_HEADER]: getTraceId(),
  };
  const user = auth.currentUser;
  if (user) {
    try {
      headers.Authorization = `Bearer ${await user.getIdToken()}`;
    } catch (err) {
      // `getIdToken()` failed — typically because the SDK is in a wedged
      // state (corrupted refresh token, `securetoken.googleapis.com` 400s
      // on every refresh). Without recovery, every page falls through to
      // a generic "request failed" message while the user is silently
      // stuck. Force a clean slate + redirect so the next sign-in mints
      // a fresh token.
      if (!recoveryInFlight) {
        recoveryInFlight = true;
        await forceAuthPurge();
        if (typeof window !== 'undefined' && window.location.pathname !== '/sign-in') {
          window.location.assign('/sign-in?reason=session-expired');
        }
      }
      throw new SessionExpiredError(err);
    }
  }
  // Multi-tenant: every authed request carries the active org id so the
  // server can scope reads/writes. Persisted in localStorage so SSE +
  // non-React call sites share the same value as the React tree.
  const activeOrgId = readActiveOrgId();
  if (activeOrgId) {
    headers['X-Org-Id'] = activeOrgId;
  }
  const appCheck = getAppCheckInstance();
  if (appCheck) {
    try {
      const { token } = await getToken(appCheck, /* forceRefresh */ false);
      if (token) headers['X-Firebase-AppCheck'] = token;
    } catch {
      // App Check couldn't produce a token (offline, reCAPTCHA hiccup,
      // network transient). Ship without — server logs, optionally
      // enforces. A retried request usually succeeds once the SDK
      // completes a fresh exchange round-trip.
    }
  }
  return headers;
}
